interface ProductsSectionProps {
  onGetFreeGuide: () => void;
}

const starterFeatures = [
  '7-step quick-start checklist',
  'Proven email template to coaches',
  'Athlete bio/profile checklist',
  'Recruiting timeline by sport',
  'Top 10 mistakes to avoid',
];

const fullFeatures = [
  '4-year freshman-to-senior roadmap',
  '12+ coach email templates',
  'Highlight video blueprint',
  'Coach contact tracker spreadsheet',
  'NCAA eligibility checklist',
  'Scholarship negotiation scripts',
  'Official visit prep guide',
  'Signing Day checklist',
];

export default function ProductsSection({ onGetFreeGuide }: ProductsSectionProps) {
  return (
    <section id="products" className="bg-[#0a1628] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-[#f5a623]/10 border border-[#f5a623]/30 rounded-full px-4 py-2 mb-6">
            <i className="ri-book-open-fill text-[#f5a623] text-sm" />
            <span className="text-[#f5a623] font-body text-xs font-semibold uppercase tracking-widest">Choose Your Playbook</span>
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white uppercase leading-tight">
            Start Free.
            <span className="block text-[#f5a623]">Go All In.</span>
          </h2>
          <p className="font-body text-lg text-white/60 max-w-xl mx-auto mt-4">
            Get started with our free guide or unlock the complete recruiting system.
          </p>
        </div>

        {/* Product cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-5xl mx-auto">
          {/* Starter Guide */}
          <div className="bg-[#122040] border border-white/10 rounded-2xl p-7 md:p-8 flex flex-col">
            <div className="flex items-start justify-between mb-6">
              <div>
                <div className="inline-flex items-center gap-1 bg-white/10 rounded-full px-3 py-1 mb-3">
                  <span className="text-white/70 font-body text-xs font-semibold uppercase tracking-wider">Starter Guide</span>
                </div>
                <h3 className="font-display text-3xl md:text-4xl font-bold text-white uppercase">The Starter Guide</h3>
              </div>
              <div className="text-right">
                <div className="font-display text-3xl font-bold text-white">Free</div>
                <div className="font-body text-xs text-white/40 line-through">$7 value</div>
              </div>
            </div>

            <p className="font-body text-white/60 text-sm mb-6 leading-relaxed">
              Everything you need to <strong className="text-white">get started today</strong> — the essential foundation before the full recruiting push begins.
            </p>

            <ul className="flex flex-col gap-3 mb-8 flex-1">
              {starterFeatures.map((feat) => (
                <li key={feat} className="flex items-start gap-3">
                  <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                    <i className="ri-checkbox-circle-fill text-[#f5a623] text-lg" />
                  </div>
                  <span className="font-body text-sm text-white/80">{feat}</span>
                </li>
              ))}
            </ul>

            <a
              href="https://stan.store/The_RecruitingPlaybook/p/get-the-free-7step-playbook-now"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full block text-center border-2 border-[#f5a623]/60 text-[#f5a623] py-4 rounded-lg font-body font-bold text-base hover:bg-[#f5a623]/10 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Download Free
            </a>
          </div>

          {/* Full Playbook */}
          <div className="relative bg-gradient-to-b from-[#f5a623] to-[#d4891a] rounded-2xl p-1">
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#0a1628] border border-[#f5a623] rounded-full px-5 py-1.5">
              <span className="font-display text-sm font-bold text-[#f5a623] uppercase tracking-widest">Most Popular</span>
            </div>
            <div className="bg-[#0f1e3a] rounded-xl p-7 md:p-8 flex flex-col h-full">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <div className="inline-flex items-center gap-1 bg-[#f5a623]/20 rounded-full px-3 py-1 mb-3">
                    <i className="ri-vip-crown-fill text-[#f5a623] text-xs" />
                    <span className="text-[#f5a623] font-body text-xs font-semibold uppercase tracking-wider">Full Playbook</span>
                  </div>
                  <h3 className="font-display text-3xl md:text-4xl font-bold text-white uppercase">The Full Playbook</h3>
                </div>
                <div className="text-right">
                  <div className="font-display text-3xl font-bold text-[#f5a623]">$47</div>
                  <div className="font-body text-xs text-white/40">One-time payment</div>
                </div>
              </div>

              <p className="font-body text-white/70 text-sm mb-6 leading-relaxed">
                The <strong className="text-white">complete system</strong> used by families who land D1 scholarships — every template, tracker, and roadmap included.
              </p>

              <ul className="flex flex-col gap-3 mb-8 flex-1">
                {fullFeatures.map((feat) => (
                  <li key={feat} className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                      <i className="ri-checkbox-circle-fill text-[#f5a623] text-lg" />
                    </div>
                    <span className="font-body text-sm text-white/90">{feat}</span>
                  </li>
                ))}
              </ul>

              <a
                href="https://stan.store/The_RecruitingPlaybook/p/get-the-recruiting-playbook-now"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full block text-center bg-[#f5a623] hover:bg-[#d4891a] text-[#0a1628] py-4 rounded-lg font-body font-bold text-base transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Get The Full Playbook — $47
              </a>
              <p className="text-center font-body text-xs text-white/40 mt-3">
                30-day money-back guarantee. No questions asked.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
